import React, { useEffect, useState } from "react";
import axios, { cookies, MEDIA_BASE_URL } from "../axios/axios";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";

const RetouchingForm = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get("/services/getServices");
        setServices(response.data.services);
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "An error occurred");
        toast.error(error.response?.data?.message || "An error occurred");
      }
    };
    fetchServices();
  }, []);

  const newData = services.find(
    (val) => val.id.replaceAll(" ", "-").replaceAll("/", "_") === id
  );

  const [data, setData] = useState({
    price: "",
    unit_price: "",
    order_name: "",
    color_correction: "",
    sharpening: "",
    skin_sofening: "",
    eyes_whitening: "",
    hands_feet_redness: "",
    teeth_whitening: "",
    background_extend: "",
    quantity: "",
    social_media: "",
    drive_link: "",
    additional_notes: "",
    output_format: "",
  });

  useEffect(() => {
    if (newData) {
      setData((prevData) => ({
        ...prevData,
        price: Number(newData.price),
        unit_price: Number(newData.price),
        quantity: prevData.quantity || "1",
      }));
    }
  }, [newData]);

  const getQuantity = () => Number(data.quantity) || 0;

  const dataHandler = (e) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      const qty = Math.max(0, Math.floor(Number(value) || 0));

      setData((prevData) => {
        const unit = Number(prevData.unit_price) || 0;
        const newPrice =
          isCouponApplied && discount
            ? Number((unit * qty * (1 - discount / 100)).toFixed(2))
            : Number((unit * qty).toFixed(2));
        return { ...prevData, quantity: qty.toString(), price: newPrice };
      });

      if (file && file.length > 0) {
        const selectedCount = file.length;
        if (qty === 1 && selectedCount > 1) {
          setFile([file[0]]);
          toast.info("Quantity set to 1 — extra selected files were removed.");
        } else if (qty > 0 && selectedCount > qty) {
          setFile(file.slice(0, qty));
          toast.info(
            `Quantity set to ${qty} — extra selected files were removed.`
          );
        }
      }
    } else {
      setData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const onFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const qty = getQuantity();

    if (qty > 0) {
      if (selectedFiles.length > qty) {
        const trimmed = selectedFiles.slice(0, qty);
        setFile(trimmed);
        toast.warn(
          `You selected more than ${qty} files — only the first ${qty} were kept.`
        );
        return;
      }

      if (selectedFiles.length < qty) {
        setFile(selectedFiles);
        toast.info(
          `You selected ${selectedFiles.length} files. Please select exactly ${qty} files before submitting.`
        );
        return;
      }

      setFile(selectedFiles);
    } else {
      setFile(selectedFiles);
      toast.info("Set quantity to enforce exact file count.");
    }
  };

  const applyCoupon = async () => {
    try {
      const storedUser = cookies.get("user");
      console.log("storedUser from cookies:", storedUser);

      const userId = storedUser?.user?.id || null;

      if (!userId) {
        toast.error("User not found. Please log in again.");
        return;
      }

      const response = await axios.post("/coupon/validate", {
        code: couponCode,
        userId,
      });

      if (response.data.valid) {
        const qty = getQuantity() || 1;
        setDiscount(response.data.discount);
        setIsCouponApplied(true);

        setData((prevData) => {
          const unit = Number(prevData.unit_price) || 0;
          const base = unit * qty;
          const newPrice = Number(
            (base * (1 - response.data.discount / 100)).toFixed(2)
          );
          return { ...prevData, price: newPrice };
        });

        toast.success("Coupon applied!");
      } else {
        setErrorMessage(response.data.message || "Coupon not valid");
        toast.error(response.data.message || "Coupon not valid");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setErrorMessage(
        error.response?.data?.message || "Error validating coupon."
      );
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const dataSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: resData } = await axios.post(`/retouching/${id}`, data);
      toast.success("Data Submitted Successfully!");
      navigate(`/upload-files/${resData.id}?type=retouching`);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!newData) {
    return <h2 className="text-center my-5">Loading...</h2>;
  }

  const qty = getQuantity();
  const originalTotal = Number(data.unit_price || 0) * (qty || 1);

  return (
    <>
      <Navbar />
      <div className="mx-5">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <div className="row align-items-center py-5">
          <div className="col-md-6 my-4">
            <img
              className="w-100 object-fit-cover"
              height={600}
              src={`${MEDIA_BASE_URL}/${newData.image}`}
              crossOrigin="anonymous"
              alt="Retouching"
            />
          </div>

          <div className="col-md-6 my-4 ps-5">
            <h4 className="mb-3">{newData.title}</h4>

            <h5 className="mb-3">
              Price:{" "}
              {isCouponApplied ? (
                <>
                  <del className="text-danger me-2">
                    ${originalTotal.toFixed(2)}
                  </del>
                  <span className="text-success">
                    ${Number(data.price).toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-dark">${originalTotal.toFixed(2)}</span>
              )}
            </h5>

            {/* Coupon Code Input */}
            <div className="row  g-3 align-items-center mb-1">
              <div className="col-md-4">
                <label className="fs-14 col-form-label">Coupon Code</label>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control shadow-none rounded-2 p-2"
                  placeholder="Enter Coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  disabled={isCouponApplied}
                />
              </div>
              <button
                className="col-md-2 btn py-2 px-0 rounded-2 btn-secondary mt-2"
                onClick={applyCoupon}
                disabled={isCouponApplied}
              >
                Apply
              </button>
            </div>
            <form onSubmit={dataSubmit}>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label htmlFor="order_name" className="fs-14 col-form-label">
                    Order Name
                  </label>
                </div>
                <div className="col-md-8">
                  <input
                    onChange={dataHandler}
                    type="text"
                    name="order_name"
                    value={data.order_name}
                    className="form-control shadow-none rounded-2 p-2"
                    required
                  />
                </div>
              </div>
              {[
                { label: "Color Correction", name: "color_correction" },
                { label: "Sharpening", name: "sharpening" },
                { label: "Skin Softening", name: "skin_sofening" },
                { label: "Eyes Whitening", name: "eyes_whitening" },
                { label: "Hands Feet Redness", name: "hands_feet_redness" },
                { label: "Teeth Whitening", name: "teeth_whitening" },
                { label: "Background Extend", name: "background_extend" },
              ].map((field, index) => (
                <div className="row g-3 align-items-center mb-1" key={index}>
                  <div className="col-md-4">
                    <label
                      htmlFor={field.name}
                      className="fs-14 col-form-label"
                    >
                      {field.label}
                    </label>
                  </div>
                  <div className="col-md-8">
                    <select
                      onChange={dataHandler}
                      name={field.name}
                      value={data[field.name]}
                      className="w-100 p-2 form-select"
                      required
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              ))}
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Social Media
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="social_media"
                    value={data.social_media}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label htmlFor="quantity" className="fs-14 col-form-label">
                    Quantity
                  </label>
                </div>
                <div className="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="quantity"
                    value={data.quantity}
                    type="number"
                    min={1}
                    className="form-control shadow-none rounded-2 p-2"
                    required
                  />
                </div>
              </div>

              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label
                    htmlFor="additional_notes"
                    className="fs-14 col-form-label"
                  >
                    Additional Notes
                  </label>
                </div>
                <div className="col-md-8">
                  <textarea
                    onChange={dataHandler}
                    name="additional_notes"
                    value={data.additional_notes}
                    className="form-control shadow-none rounded-2 p-2"
                  ></textarea>
                </div>
              </div>
              <button
                type="submit"
                className="btn bg_maincolor text-white"
                disabled={loading}
              >
                {loading ? "Loading..." : "Order Now"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RetouchingForm;
